import { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useTranslation } from 'react-i18next';
import remarkGfm from 'remark-gfm';
import api from '../../api/client';

const markdownComponents = {
  h1: ({ node: _node, ...props }) => (
    <h1 className="mt-6 text-h2 first:mt-0" {...props} />
  ),
  h2: ({ node: _node, ...props }) => (
    <h2 className="mt-6 text-h2 first:mt-0" {...props} />
  ),
  h3: ({ node: _node, ...props }) => (
    <h3 className="mt-5 font-bold text-text-primary first:mt-0" {...props} />
  ),
  p: ({ node: _node, ...props }) => (
    <p className="leading-relaxed" {...props} />
  ),
  ul: ({ node: _node, ...props }) => (
    <ul className="list-outside list-disc space-y-2 pl-6" {...props} />
  ),
  ol: ({ node: _node, ...props }) => (
    <ol className="list-outside list-decimal space-y-2 pl-6" {...props} />
  ),
  li: ({ node: _node, ...props }) => (
    <li className="pl-1 marker:text-text-secondary" {...props} />
  ),
  a: ({ node: _node, ...props }) => (
    <a
      className="break-words text-icon-accent underline decoration-icon-accent/60 underline-offset-2"
      {...props}
    />
  ),
  strong: ({ node: _node, ...props }) => (
    <strong className="font-bold text-text-primary" {...props} />
  ),
  hr: ({ node: _node, ...props }) => (
    <hr className="my-6 border-0 border-t border-border-default" {...props} />
  ),
  blockquote: ({ node: _node, ...props }) => (
    <blockquote
      className="border-l-checkbox border-icon-accent pl-4 text-text-secondary"
      {...props}
    />
  ),
  table: ({ node: _node, ...props }) => (
    <div className="legal-markdown-table-scroll">
      <table
        className="w-max min-w-full border-separate border-spacing-0 text-left"
        {...props}
      />
    </div>
  ),
  thead: ({ node: _node, ...props }) => (
    <thead className="bg-surface-2 text-text-primary" {...props} />
  ),
  tbody: ({ node: _node, ...props }) => <tbody {...props} />,
  tr: ({ node: _node, ...props }) => (
    <tr className="even:bg-surface-5" {...props} />
  ),
  th: ({ node: _node, ...props }) => (
    <th
      className="whitespace-nowrap border-b border-r border-border-default px-4 py-3 font-bold last:border-r-0"
      {...props}
    />
  ),
  td: ({ node: _node, ...props }) => (
    <td
      className="max-w-72 border-b border-r border-border-default px-4 py-3 align-top leading-relaxed last:border-r-0"
      {...props}
    />
  ),
};

function getTelegramSafeAreaTop() {
  if (typeof window === 'undefined') return 0;
  const webApp = window.Telegram?.WebApp;
  return Math.max(
    Number(webApp?.safeAreaInset?.top) || 0,
    Number(webApp?.contentSafeAreaInset?.top) || 0,
  );
}

export default function LegalDocumentViewer({ slug, locale, onClose }) {
  const { t } = useTranslation();
  const [document, setDocument] = useState(null);
  const [error, setError] = useState(null);
  const [telegramSafeAreaTop, setTelegramSafeAreaTop] = useState(getTelegramSafeAreaTop);

  useEffect(() => {
    let active = true;
    setDocument(null);
    setError(null);

    api.get(`/legal/${slug}`, { params: { locale } })
      .then((response) => {
        if (active) setDocument(response.data);
      })
      .catch((requestError) => {
        if (active) setError(requestError);
      });

    return () => {
      active = false;
    };
  }, [locale, slug]);

  useEffect(() => {
    const webApp = window.Telegram?.WebApp;
    const updateSafeArea = () => setTelegramSafeAreaTop(getTelegramSafeAreaTop());

    updateSafeArea();
    webApp?.onEvent?.('safeAreaChanged', updateSafeArea);
    webApp?.onEvent?.('contentSafeAreaChanged', updateSafeArea);

    return () => {
      webApp?.offEvent?.('safeAreaChanged', updateSafeArea);
      webApp?.offEvent?.('contentSafeAreaChanged', updateSafeArea);
    };
  }, []);

  useEffect(() => {
    const notifyHeaderControlsChanged = () => {
      window.dispatchEvent(new Event('legal-document-viewer-change'));
    };
    const closeDocument = () => onClose();
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') onClose();
    };

    window.addEventListener('legal-document-viewer-close', closeDocument);
    window.addEventListener('keydown', closeOnEscape);
    notifyHeaderControlsChanged();

    return () => {
      window.removeEventListener('legal-document-viewer-close', closeDocument);
      window.removeEventListener('keydown', closeOnEscape);
      window.setTimeout(notifyHeaderControlsChanged, 0);
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[80] flex flex-col bg-bg-app text-text-primary"
      style={{ '--legal-safe-top': `${telegramSafeAreaTop}px` }}
      role="dialog"
      aria-modal="true"
      aria-label={t(`consent.documents.${slug}`)}
      data-testid="legal-document-viewer"
    >
      <main className="legal-document-body min-h-0 flex-1 overflow-y-auto px-body-x pb-6">
        {!document && !error && (
          <p className="font-sans text-plan text-text-secondary">{t('document.loading')}</p>
        )}
        {error && (
          <p className="font-sans text-plan text-text-secondary" role="alert">
            {t('document.error')}
          </p>
        )}
        {document && (
          <>
            {document.isFallback && (
              <p
                className="mb-5 rounded-lg bg-surface-2 px-4 py-3 font-sans text-plan text-text-secondary"
                data-testid="legal-fallback-notice"
              >
                {t('consent.fallbackNotice')}
              </p>
            )}
            <article className="space-y-4 font-sans text-plan leading-relaxed text-text-primary select-text">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={markdownComponents}
              >
                {document.content}
              </ReactMarkdown>
            </article>
          </>
        )}
      </main>
    </div>
  );
}
